import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, ClipboardList, ArrowRight, Sparkles, Store, AlertCircle, Plus, X, BarChart3, Trash2, TrendingUp } from "lucide-react";
import { SurveysTable } from "@/pages/admin/AdminSurveys";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import ResidenceVerificationCard from "@/components/ResidenceVerificationCard";

export default function CommercantDashboard() {
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [mySurveys, setMySurveys] = useState([]);
  const [profile, setProfile] = useState(null);
  const [creatingSurvey, setCreatingSurvey] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [results, setResults] = useState(null);

  const load = () => {
    api.get("/vouchers/merchant").then((r) => setVouchers(r.data)).catch(() => {});
    api.get("/merchant/surveys").then((r) => setMySurveys(r.data)).catch(() => {});
    api.get("/merchant/profile").then((r) => setProfile(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const redeem = async (id) => {
    try { await api.post(`/vouchers/${id}/redeem`); toast.success("Bon validé ✓"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openResults = async (s) => {
    setViewing(s);
    try {
      const { data } = await api.get(`/surveys/${s.id}/results`);
      setResults(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closeSurvey = async (id) => {
    if (!window.confirm("Clore définitivement ce sondage ?")) return;
    try { await api.delete(`/surveys/${id}`); toast.success("Sondage clos."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const totalIssued = vouchers.length;
  const totalRedeemed = vouchers.filter((v) => v.redeemed).length;
  const conversionRate = totalIssued > 0 ? Math.round((totalRedeemed / totalIssued) * 100) : 0;
  const pendingMySurveys = mySurveys.filter((s) => s.status === "pending").length;
  const activeMySurveys = mySurveys.filter((s) => s.status === "active").length;

  return (
    <div className="space-y-5" data-testid="commercant-dashboard">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="phenix-pill bg-sun-100 text-sun-700 mb-2">
            <Sparkles size={11} strokeWidth={2.5} /> {user.merchant_category || "Commerce local"}
          </div>
          <h1 className="heading-display text-xl sm:text-2xl">{user.merchant_name || user.name}</h1>
          <p className="text-xs text-plum-700/65">Pilotez vos sondages, vos bons et votre fiche.</p>
        </div>
        <div className="phenix-card px-4 py-2.5 bg-gradient-to-br from-sun-300 to-sun-400 text-plum-900 border-0 flex items-center gap-3">
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-plum-700/70">Conversion</div>
            <div className="font-display font-extrabold text-2xl leading-none">{conversionRate}%</div>
          </div>
          <div className="text-[10px] text-plum-700/70 max-w-[100px] leading-tight">{totalRedeemed} bons utilisés sur {totalIssued}</div>
        </div>
      </header>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="merchant-voucher-stats">
        <Stat icon="🎫" color="bg-sun-100 text-sun-700" label="Bons émis" value={totalIssued} />
        <Stat icon="✅" color="bg-mint-100 text-mint-600" label="Utilisés" value={totalRedeemed} />
        <Stat icon="📊" color="bg-plum-100 text-plum-600" label="Conversion" value={`${conversionRate}%`} />
        <Stat icon="🗳️" color="bg-pink-100 text-pink-600" label="Sondages actifs" value={activeMySurveys} />
      </section>

      {/* Mes sondages — tableau */}
      <section className="phenix-card p-4 sm:p-5 bg-white" data-testid="merchant-my-surveys">
        <div className="flex items-start justify-between gap-3 mb-3.5 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-plum-600 text-white grid place-items-center shrink-0 shadow-sm">
              <ClipboardList size={18} strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <h2 className="font-display font-bold text-base sm:text-lg text-plum-900 leading-tight">Mes sondages</h2>
              <p className="text-[12px] text-plum-700/70 mt-0.5">
                Posez une question à vos clients, avec ou sans bon de réduction. Les sondages avec bon passent en modération mairie.
              </p>
            </div>
          </div>
          <Button onClick={() => setCreatingSurvey(true)} className="btn-pill bg-plum-700 hover:bg-plum-800 text-white h-10 px-4 text-[13px]" data-testid="merchant-new-survey-btn">
            <Plus size={14} className="mr-1.5" /> Lancer un sondage
          </Button>
        </div>

        {pendingMySurveys > 0 && (
          <div className="rounded-xl bg-sun-50 border border-sun-200 px-3 py-2 mb-3 text-[12px] text-sun-700 font-semibold">
            ⏳ {pendingMySurveys} sondage{pendingMySurveys > 1 ? "s" : ""} en attente de validation par la mairie.
          </div>
        )}

        {mySurveys.length === 0 ? (
          <div className="border-2 border-dashed border-cream-200 rounded-2xl p-8 text-center">
            <ClipboardList size={28} className="mx-auto text-plum-700/30 mb-2" strokeWidth={1.8} />
            <p className="text-[13px] text-plum-700/65 mb-3">
              Aucun sondage encore. Lancez votre premier sondage pour engager vos clients.
            </p>
            <Button onClick={() => setCreatingSurvey(true)} size="sm" className="btn-pill bg-plum-700 hover:bg-plum-800 text-white" data-testid="merchant-new-survey-empty-btn">
              <Plus size={12} className="mr-1.5" /> Lancer un sondage
            </Button>
          </div>
        ) : (
          <SurveysTable
            surveys={mySurveys}
            showActions
            actions={{
              onResults: openResults,
              onClose: closeSurvey,
            }}
          />
        )}
      </section>

      <section className="grid sm:grid-cols-3 gap-2.5">
        <Tile to="/commercant/stats" icon={TrendingUp} title="Mes statistiques" desc="Engagement, conversion, top posts" color="bg-mint-500" />
        <Tile to="/remontees" icon={MessageSquare} title="Envoyer une remontée" desc="Demande d'info à la mairie" color="bg-pink-400" />
        <Tile to="/sondages" icon={ClipboardList} title="Sondages publics" desc="Voir tous les sondages" color="bg-plum-600" />
      </section>

      <PushNotificationToggle />

      {/* Justificatif de domicile (co-voiturage) */}
      <ResidenceVerificationCard />

      {/* Ma fiche commerçant — compact banner */}
      <section data-testid="merchant-profile-banner">
        {profile && profile.description && profile.logo_url ? (
          <Link to="/commercant/fiche" className="phenix-card phenix-card-hover p-3 flex items-center gap-3 group">
            <div className="w-11 h-11 rounded-full overflow-hidden bg-cream-100 shrink-0 grid place-items-center">
              <img src={profile.logo_url} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="phenix-pill bg-mint-100 text-mint-600 mb-0.5 text-[10px]">Fiche publiée ✓</div>
              <h3 className="font-display font-bold text-sm text-plum-900 truncate">{profile.merchant_name}</h3>
              <p className="text-[11px] text-plum-700/70 line-clamp-1">{profile.description}</p>
            </div>
            <ArrowRight size={15} className="text-plum-400 group-hover:text-plum-700 group-hover:translate-x-0.5 transition-all shrink-0" />
          </Link>
        ) : (
          <Link to="/commercant/fiche" className="phenix-card phenix-card-hover p-3.5 flex items-center gap-3 group border-2 border-dashed border-sun-300 bg-sun-50/40">
            <div className="bg-sun-400 text-plum-900 w-10 h-10 rounded-xl grid place-items-center shadow-sm shrink-0">
              <Store size={17} strokeWidth={2.2} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="font-display font-bold text-sm text-plum-900">Complétez votre fiche</h3>
                <AlertCircle size={13} className="text-sun-600" strokeWidth={2.5} />
              </div>
              <p className="text-[12px] text-plum-700/70 line-clamp-1">
                Logo, horaires, adresse, description → apparaître dans l&apos;annuaire d&apos;Istres.
              </p>
            </div>
            <ArrowRight size={14} className="text-plum-400 shrink-0" />
          </Link>
        )}
      </section>

      <section data-testid="merchant-feedback-list">
        <h2 className="font-display font-bold text-base text-plum-900 mb-2.5">Bons à valider</h2>
        {vouchers.length === 0 ? (
          <div className="phenix-card p-4 text-center text-plum-700/50 text-xs">
            Aucun bon émis pour l&apos;instant.
          </div>
        ) : (
          <div className="space-y-1.5">
            {vouchers.map((v) => (
              <div key={v.id} className="phenix-card p-3 flex flex-wrap items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-sun-100 text-sun-700 grid place-items-center text-base shrink-0">🎫</div>
                <div className="flex-1 min-w-[180px]">
                  <div className="font-display font-bold text-plum-900 text-sm">-{v.percent}% · {v.label}</div>
                  <div className="text-[11px] text-plum-700/60">
                    {v.holder.pseudo} · <span className="font-mono">{v.code}</span>
                  </div>
                </div>
                {v.redeemed ? (
                  <span className="phenix-pill bg-mint-100 text-mint-600 text-[10px]">✓ Utilisé</span>
                ) : (
                  <Button size="sm" onClick={() => redeem(v.id)}
                    className="btn-pill bg-plum-600 hover:bg-plum-700 text-white h-8 px-3 text-xs"
                    data-testid={`redeem-voucher-${v.id}`}>
                    Valider <ArrowRight size={12} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <MerchantSurveyDialog open={creatingSurvey} onClose={() => setCreatingSurvey(false)} onCreated={load} merchantName={user.merchant_name || user.name} />
      <ResultsDialog open={!!viewing} survey={viewing} results={results}
        onClose={() => { setViewing(null); setResults(null); }} />
    </div>
  );
}

function MerchantSurveyDialog({ open, onClose, onCreated, merchantName }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState([{ question: "", options: ["", ""] }]);
  const [withVoucher, setWithVoucher] = useState(true);
  const [vouchersCount, setVouchersCount] = useState("30");
  const [voucherPercent, setVoucherPercent] = useState("10");
  const [voucherLabel, setVoucherLabel] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && merchantName && !voucherLabel) {
      setVoucherLabel(`-10 % chez ${merchantName}`);
    }
  }, [open, merchantName]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setTitle(""); setDescription("");
    setQuestions([{ question: "", options: ["", ""] }]);
    setWithVoucher(true); setVouchersCount("30"); setVoucherPercent("10");
    setVoucherLabel(merchantName ? `-10 % chez ${merchantName}` : "");
  };

  const addQuestion = () => setQuestions([...questions, { question: "", options: ["", ""] }]);
  const removeQuestion = (qi) => setQuestions(questions.filter((_, i) => i !== qi));
  const updateQ = (qi, field, val) => { const next = [...questions]; next[qi][field] = val; setQuestions(next); };
  const updateOpt = (qi, oi, val) => { const next = [...questions]; next[qi].options[oi] = val; setQuestions(next); };
  const addOpt = (qi) => { const next = [...questions]; next[qi].options.push(""); setQuestions(next); };
  const removeOpt = (qi, oi) => { const next = [...questions]; if (next[qi].options.length > 2) { next[qi].options.splice(oi, 1); setQuestions(next); } };

  const submit = async () => {
    if (!title.trim() || !description.trim()) return toast.error("Titre et description requis");
    for (const q of questions) {
      if (!q.question.trim()) return toast.error("Toutes les questions doivent être remplies");
      if (q.options.some((o) => !o.trim())) return toast.error("Toutes les options doivent être remplies");
    }
    if (withVoucher && (!voucherLabel.trim() || Number(vouchersCount) <= 0)) {
      return toast.error("Libellé du bon et nombre requis (ou décochez l'option bon).");
    }
    setBusy(true);
    try {
      const res = await api.post("/surveys", {
        title, description, target: "habitants", questions,
        vouchers_count: withVoucher ? Number(vouchersCount) || 0 : 0,
        voucher_label: withVoucher ? voucherLabel : null,
        voucher_percent: withVoucher ? Number(voucherPercent) || 0 : 0,
      });
      if (res.data.status === "pending") {
        toast.success("Sondage envoyé à la mairie pour validation.");
      } else {
        toast.success("Sondage publié auprès des habitants !");
      }
      reset(); onClose(); onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-cream-50 border-cream-200 rounded-xl" data-testid="merchant-survey-dialog">
        <DialogHeader>
          <DialogTitle className="heading-display text-lg text-plum-900">
            <em className="text-pink-500">Nouveau</em> sondage commerçant
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Titre</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="m-survey-title" placeholder="ex. Quel pain spécial cet été ?" className="rounded-xl border-cream-200 bg-transparent h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Description</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="m-survey-desc" placeholder="Expliquez le contexte en 1-2 phrases…" className="rounded-xl border-cream-200 bg-transparent" />
          </div>

          {/* Voucher toggle */}
          <div className="rounded-2xl border border-cream-200 bg-white p-4">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="m-survey-with-voucher-toggle">
              <input type="checkbox" checked={withVoucher} onChange={(e) => setWithVoucher(e.target.checked)}
                className="w-5 h-5 accent-plum-700" />
              <div>
                <div className="font-display font-bold text-plum-900 text-[14px]">Offrir un bon de réduction en échange ?</div>
                <div className="text-[11px] text-plum-700/65">Sans bon : publication directe. Avec bon : validation par la mairie sous 24-48h.</div>
              </div>
            </label>

            {withVoucher && (
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-plum-700/70">Nb de bons</Label>
                  <Input type="number" min="1" max="500" value={vouchersCount}
                    onChange={(e) => setVouchersCount(e.target.value)} data-testid="m-survey-vouchers-count"
                    className="rounded-xl border-cream-200 bg-transparent h-10" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-plum-700/70">% de réduction</Label>
                  <Input type="number" min="0" max="100" value={voucherPercent}
                    onChange={(e) => setVoucherPercent(e.target.value)} data-testid="m-survey-voucher-percent"
                    className="rounded-xl border-cream-200 bg-transparent h-10" />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-plum-700/70">Libellé visible par le client</Label>
                  <Input value={voucherLabel} onChange={(e) => setVoucherLabel(e.target.value)}
                    data-testid="m-survey-voucher-label"
                    placeholder={`ex. -10 % chez ${merchantName || "ma boutique"}`}
                    className="rounded-xl border-cream-200 bg-transparent h-10" />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Questions</Label>
              <button onClick={addQuestion} className="btn-ghost-line text-xs"><Plus size={12} /> Ajouter</button>
            </div>
            {questions.map((q, qi) => (
              <div key={qi} className="border border-cream-200 p-4 space-y-3 bg-cream-25 rounded-xl">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-plum-700/50">{String(qi + 1).padStart(2, "0")}</span>
                  <Input value={q.question} onChange={(e) => updateQ(qi, "question", e.target.value)}
                    placeholder={`Question ${qi + 1}`} data-testid={`m-survey-q-${qi}`}
                    className="rounded-xl border-cream-200 bg-transparent h-10" />
                  {questions.length > 1 && (
                    <button onClick={() => removeQuestion(qi)} className="text-plum-700/50 hover:text-pink-500"><X size={14} /></button>
                  )}
                </div>
                <div className="space-y-2 pl-7">
                  {q.options.map((o, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-plum-700/50 w-5">{String.fromCharCode(65 + oi)}</span>
                      <Input value={o} onChange={(e) => updateOpt(qi, oi, e.target.value)}
                        placeholder={`Option ${oi + 1}`} data-testid={`m-survey-q${qi}-opt${oi}`}
                        className="rounded-xl border-cream-200 bg-transparent h-9" />
                      {q.options.length > 2 && (
                        <button onClick={() => removeOpt(qi, oi)} className="text-plum-700/50 hover:text-pink-500"><X size={12} /></button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => addOpt(qi)} className="btn-ghost-line text-[11px] ml-7"><Plus size={10} /> Option</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Annuler</Button>
          <Button onClick={submit} disabled={busy} className="btn-pill bg-plum-700 hover:bg-plum-800 text-white" data-testid="m-survey-submit">
            {withVoucher ? "Envoyer pour validation" : "Publier maintenant"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultsDialog({ open, survey, results, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-cream-50 border-cream-200 rounded-xl">
        <DialogHeader>
          <DialogTitle className="heading-display text-lg text-plum-900">{survey?.title}</DialogTitle>
        </DialogHeader>
        {!results ? (
          <p className="text-plum-700/50 italic">Chargement...</p>
        ) : results.total_responses === 0 ? (
          <p className="text-plum-700/50 text-center py-6 italic">Aucune réponse pour le moment.</p>
        ) : (
          <div className="space-y-6">
            <div className="border-b border-cream-200 pb-3">
              <div className="phenix-eyebrow mb-2">Réponses</div>
              <div className="font-display text-3xl text-plum-900">
                {results.total_responses}<span className="text-pink-500 text-lg font-light"> au total</span>
              </div>
            </div>
            {results.results.map((r, ri) => {
              const total = r.counts.reduce((a, b) => a + b, 0) || 1;
              return (
                <div key={ri}>
                  <div className="font-display font-bold text-base text-plum-900 mb-3">
                    {String(ri + 1).padStart(2, "0")}. {r.question}
                  </div>
                  <div className="space-y-2.5">
                    {r.options.map((opt, oi) => {
                      const pct = Math.round((r.counts[oi] / total) * 100);
                      return (
                        <div key={oi}>
                          <div className="flex items-center justify-between text-[13px] mb-1">
                            <span className="text-plum-900">{opt}</span>
                            <span className="font-mono text-[11px] text-plum-700/70 tabular-nums">{r.counts[oi]} · {pct}%</span>
                          </div>
                          <div className="h-2 bg-cream-100 rounded-full overflow-hidden">
                            <div className="h-full bg-plum-600 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ icon, color, label, value }) {
  return (
    <div className="phenix-card p-2.5">
      <div className={`w-7 h-7 rounded-lg grid place-items-center ${color} text-sm mb-1.5`}>{icon}</div>
      <div className="font-display font-extrabold text-lg text-plum-900 leading-none">{value}</div>
      <div className="text-[10px] text-plum-700/60 mt-0.5">{label}</div>
    </div>
  );
}

function Tile({ to, icon: Icon, title, desc, color }) {
  return (
    <Link to={to} className="phenix-card phenix-card-hover p-3 group flex items-center gap-3">
      <div className={`${color} text-white w-9 h-9 rounded-xl grid place-items-center shadow-sm shrink-0`}>
        <Icon size={15} strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-display font-bold text-sm text-plum-900 leading-tight">{title}</h3>
        <p className="text-[11px] text-plum-700/65 line-clamp-1">{desc}</p>
      </div>
      <ArrowRight size={14} className="text-plum-400 group-hover:text-plum-600 group-hover:translate-x-0.5 transition-all" />
    </Link>
  );
}
